import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import dropdownIcon from '../assets/icons/light/dropdown-icon.png';
import { useParams, useNavigate } from 'react-router-dom';
import { format, subDays, startOfMonth, endOfMonth, subMonths, isSameDay, isSameMonth, isSameYear, isAfter, isBefore } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import calendarIcon from '../assets/icons/light/calender-icon.png';
import styles from './AwsCostDashboard.module.css';
import { Button, MenuItem, Select, FormControl, IconButton, Box } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import filterIcon from '../assets/icons/light/filter-icon.png';

const AwsCostDashboard = () => {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const filterRef = useRef(null);

  // Main state variables (Used for API calls)
  const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  const currentDate = new Date();
  const [startDate, setStartDate] = useState(firstDayOfMonth);
  const [endDate, setEndDate] = useState(currentDate);
  const [region, setRegion] = useState("All Regions");

  // Temporary filter state variables
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [tempRegion, setTempRegion] = useState(region || 'All Regions');
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
  const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState('');

  const [costData, setCostData] = useState([]);
  const [totalCost, setTotalCost] = useState('0.00');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isTimeExpanded, setIsTimeExpanded] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  
  // Separate tax row from the main data
  const { regularRows, taxRow } = useMemo(() => {
    const regular = costData.filter(item => !item.isBold);
    const tax = costData.find(item => item.isBold) || { service: 'Tax', cost: 0 };
    return { regularRows: regular, taxRow: tax };
  }, [costData]);
  
  // Calculate paginated data without the tax row
  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return regularRows.slice(start, start + rowsPerPage);
  }, [regularRows, page, rowsPerPage]);
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // AWS Regions with their display names
  const regions = ["All Regions", "us-east-1", "us-east-2", "us-west-1", "us-west-2",
    "ap-south-1", "ap-northeast-3", "ap-northeast-2", "ap-southeast-1",
    "ap-southeast-2", "ap-northeast-1", "ca-central-1", "eu-central-1",
    "eu-west-1", "eu-west-2", "eu-west-3", "eu-north-1", "sa-east-1"];

  // Helper function to get region display name
  const getRegionName = (regionCode) => {
    const regionNames = {
      'us-east-1': 'US East (N. Virginia)',
      'us-east-2': 'US East (Ohio)',
      'us-west-1': 'US West (N. California)',
      'us-west-2': 'US West (Oregon)',
      'ap-south-1': 'Asia Pacific (Mumbai)',
      'ap-northeast-3': 'Asia Pacific (Osaka-Local)',
      'ap-northeast-2': 'Asia Pacific (Seoul)',
      'ap-southeast-1': 'Asia Pacific (Singapore)',
      'ap-southeast-2': 'Asia Pacific (Sydney)',
      'ap-northeast-1': 'Asia Pacific (Tokyo)',
      'ca-central-1': 'Canada (Central)',
      'eu-central-1': 'EU (Frankfurt)',
      'eu-west-1': 'EU (Ireland)',
      'eu-west-2': 'EU (London)',
      'eu-west-3': 'EU (Paris)',
      'eu-north-1': 'EU (Stockholm)',
      'sa-east-1': 'South America (São Paulo)'
    };
    return regionNames[regionCode] || regionCode;
  };

  // Helper function to format date as YYYY-MM-DD from local date components
  const formatDateForApi = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // getMonth() is 0-indexed
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to format the end date for AWS API (exclusive end date, so add 1 day)
  const formatEndDateForApi = (date) => {
    if (!date) return null;
    const nextDay = new Date(date);
    nextDay.setDate(date.getDate() + 1);
    return formatDateForApi(nextDay);
  };

  useEffect(() => {
    fetchCostData();
  }, [startDate, endDate, region]); // Fetch data only when main state updates

  const fetchCostData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/billing-data`, {
        params: {
          account_id: accountId,
          start_date: formatDateForApi(startDate),
          end_date: formatEndDateForApi(endDate),
          region: region === "All Regions" ? null : region,
        },
      });
  
      const rawItems = response.data.cost_data || [];
      const totalCostFromApi = response.data.total_cost || 0;
  
      // Deduplicate services by summing costs
      const grouped = {};
      rawItems.forEach(item => {
        const key = item.service.trim();
        if (!grouped[key]) {
          grouped[key] = {
            service: item.service,
            cost: 0,
            isBold: item.isBold || false,
          };
        }
        grouped[key].cost += parseFloat(item.cost || 0);
      });
  
      let groupedItems = Object.values(grouped);
  
      // Separate tax
      const taxIndex = groupedItems.findIndex(item => item.service.toLowerCase() === "tax");
      let taxItem = null;
      if (taxIndex !== -1) {
        taxItem = groupedItems.splice(taxIndex, 1)[0];
      }
  
      // Sort and push tax at the end
      groupedItems.sort((a, b) => b.cost - a.cost);
      if (taxItem) {
        groupedItems.push({ ...taxItem, isBold: true });
      }
  
      setCostData(groupedItems);
      setTotalCost(Number(totalCostFromApi).toFixed(2));
    } catch (error) {
      console.error("Error fetching billing data:", error);
    }
  };
  
  // Function to update main state on Apply button click
  const applyFilters = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setRegion(tempRegion);
    setIsFilterOpen(false); // Close filter modal
  };

  // Clear filters handler is defined below with the date filter functions

  const handleDateRangeSelect = (range) => {
    const today = new Date();
    let newStartDate = new Date();
    
    setSelectedDateRange(range);
    
    switch(range) {
      case '1 Day':
        newStartDate = subDays(today, 1);
        break;
      case '7 Days':
        newStartDate = subDays(today, 7);
        break;
      case 'Month to Date':
        newStartDate = startOfMonth(today);
        break;
      case '1 Month':
        newStartDate = subMonths(today, 1);
        break;
      case '2 Months':
        newStartDate = subMonths(today, 2);
        break;
      case '3 Months':
        newStartDate = subMonths(today, 3);
        break;
      case '4 Months':
        newStartDate = subMonths(today, 4);
        break;
      case '5 Months':
        newStartDate = subMonths(today, 5);
        break;
      case '6 Months':
        newStartDate = subMonths(today, 6);
        break;
      default:
        return;
    }
    
    setTempStartDate(newStartDate);
    setTempEndDate(today);
  };

  // Predefined Date Filters
  const applyDateFilter = (filterType) => {
    const today = new Date();
    let newStartDate, newEndDate;

    switch (filterType) {
      case "1day":
        newStartDate = new Date(today);
        newStartDate.setDate(today.getDate() - 1);
        newEndDate = new Date(today);
        break;
      case "7days":
        newStartDate = new Date(today);
        newStartDate.setDate(today.getDate() - 6);
        newEndDate = new Date(today);
        break;
      case "monthToDate":
        newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
        newEndDate = new Date(today);
        break;
      case "1month":
        newStartDate = new Date(today);
        newStartDate.setMonth(today.getMonth() - 1);
        newEndDate = new Date(today);
        break;
      case "2months":
        newStartDate = new Date(today);
        newStartDate.setMonth(today.getMonth() - 2);
        newEndDate = new Date(today);
        break;
      case "3months":
        newStartDate = new Date(today);
        newStartDate.setMonth(today.getMonth() - 3);
        newEndDate = new Date(today);
        break;
      case "4months":
        newStartDate = new Date(today);
        newStartDate.setMonth(today.getMonth() - 4);
        newEndDate = new Date(today);
        break;
      default:
        return;
    }

    setTempStartDate(newStartDate);
    setTempEndDate(newEndDate);
  };

  const clearFilters = () => {
    const defaultStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    const defaultEnd = new Date();
    const defaultRegion = "All Regions";
    
    // Update temp states
    setTempStartDate(defaultStart);
    setTempEndDate(defaultEnd);
    setTempRegion(defaultRegion);
    setSelectedDateRange('');
  
    // Update main states
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setRegion(defaultRegion);
  
    // Close the filter modal
    setIsFilterOpen(false);
  };

  return (
    <div className={styles['dashboard-wrapper']}>
      <a 
        className={styles['back-link']} 
        onClick={() => navigate(-1)}
      >
        &lt; Back
      </a>
      <div className={styles['header-section']}>
        <div>
          <h1 className={styles['dashboard-title']}>AWS Cost Dashboard - {accountId}</h1>
          <div className={styles['title-divider']}></div>
          
          <div className={styles['total-cost-container']}>
            <span className={styles['total-cost-label']}>Total Cost (USD):</span>
            <span className={styles['total-cost-value']}>${totalCost || '0.00'}</span>
          </div>
        </div>
        
        <div className={styles['breadcrumb']}>
          <span 
            className={styles['breadcrumb-item']}
            onClick={() => navigate('/home')}
          >
            Dashboard
          </span>
          <span className={styles['breadcrumb-separator']}>&gt;</span>
          <span 
            className={styles['breadcrumb-item']}
            onClick={() => navigate('/monitoring-dashboard')}
          >
            Monitoring
          </span>
          <span className={styles['breadcrumb-separator']}>&gt;</span>
          <span className={styles['breadcrumb-item']}>
            {accountId === '519510601754' 
              ? 'AWS VaidhyaMegha Account - 1 - Cost'
              : accountId === '220474796007'
              ? 'AWS VaidhyaMegha Account - 2 - Cost'
              : `AWS VaidhyaMegha Account - ${accountId} - Cost`}
          </span>
        </div>
      </div>

      {/* Filter Icon */}
      <div className={styles['filter-icon-container']} onClick={() => setIsFilterOpen(true)}>
        <img 
          src={filterIcon}
          alt="Filter"
          className={styles['filter-icon']}
        />
      </div>
      
      <div className={`${styles['dashboard-container']} aws-cost-dashboard`}>
        <table className={styles['cost-table']}>
          <thead>
            <tr>
              <th>Service</th>
              <th>Cost (USD)</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRows.map((item, index) => (
              <tr key={index}>
                <td>{item.service}</td>
                <td>${parseFloat(item.cost || 0).toFixed(2)}</td>
              </tr>
            ))}
            {taxRow && (
              <tr className={styles['bold-row']}>
                <td>{taxRow.service || "Tax"}</td>
                <td>${parseFloat(taxRow.cost || 0).toFixed(2)}</td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className={styles['pagination-row']}>
              <td colSpan="2" className={styles['pagination-cell']}>
                <div className={styles['pagination-container']}>
                  <Button 
                    variant="outlined" 
                    color="primary" 
                    onClick={() => navigate("/aws-accounts")}
                    className={styles['back-to-accounts']}
                  >
                    Back to Accounts
                  </Button>
                  <div className={styles['rows-per-page']}>
                    <span>Rows per page:</span>
                    <select
                      className={styles['rows-select']}
                      value={rowsPerPage}
                      onChange={(e) => setRowsPerPage(Number(e.target.value))}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={15}>15</option>
                    </select>
                  </div>
                  <span className={styles['page-info']}>
                    {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, regularRows.length)} of {regularRows.length}
                  </span>
                  <div className={styles['pagination-buttons']}>
                    <IconButton 
                      onClick={() => setPage(p => Math.max(p - 1, 0))}
                      disabled={page === 0}
                      className={styles['pagination-chevron']}
                    >
                      <ChevronLeft />
                    </IconButton>
                    <IconButton
                      onClick={() => setPage(p => Math.min(p + 1, Math.ceil(regularRows.length / rowsPerPage) - 1))}
                      disabled={page >= Math.ceil(regularRows.length / rowsPerPage) - 1}
                      className={styles['pagination-chevron']}
                    >
                      <ChevronRight />
                    </IconButton>
                  </div>
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      
      {/* Filter Modal */}
      {isFilterOpen && (
        <div className={styles['filter-modal-overlay']} onClick={() => setIsFilterOpen(false)}>
          <div className={styles['filter-modal']} onClick={e => e.stopPropagation()}>
            <div className={styles['modal-header']}>
              <h2>Filter Options</h2>
              <button 
                className={styles['close-button']} 
                onClick={() => setIsFilterOpen(false)}
              >
                ×
              </button>
            </div>
            
            <div className={styles['modal-content']}>
              <div className={styles['form-group']}>
                <label className={styles['time-label']}>Time</label>
                <div className={styles['date-pickers-container']}>
                  <div className={styles['date-picker-group']}>
                    <div className={styles['date-picker-wrapper']}>
                      <DatePicker
                        selected={tempStartDate}
                        onChange={(date) => {
                          setTempStartDate(date);
                          setIsStartDatePickerOpen(false);
                        }}
                        onCalendarOpen={() => {
                          setIsStartDatePickerOpen(true);
                          setIsEndDatePickerOpen(false);
                        }}
                        onCalendarClose={() => setIsStartDatePickerOpen(false)}
                        open={isStartDatePickerOpen}
                        onClickOutside={() => setIsStartDatePickerOpen(false)}
                        onInputClick={() => {
                          if (isStartDatePickerOpen) {
                            setIsStartDatePickerOpen(false);
                          } else {
                            setIsStartDatePickerOpen(true);
                            setIsEndDatePickerOpen(false);
                          }
                        }}
                        selectsStart
                        startDate={tempStartDate}
                        endDate={tempEndDate}
                        maxDate={new Date()}
                        className={styles['date-picker-input']}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Start Date"
                        popperProps={{
                          positionFixed: true,
                          modifiers: [{
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                              padding: 5,
                            },
                          }],
                        }}
                        popperClassName={styles['date-picker-popper']}
                      />
                      <img 
                        src={calendarIcon} 
                        alt="Calendar" 
                        className={styles['calendar-icon']} 
                        onClick={() => {
                          if (isStartDatePickerOpen) {
                            setIsStartDatePickerOpen(false);
                          } else {
                            setIsStartDatePickerOpen(true);
                            setIsEndDatePickerOpen(false);
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className={styles['date-picker-group']}>
                    <div className={styles['date-picker-wrapper']}>
                      <DatePicker
                        selected={tempEndDate}
                        onChange={(date) => {
                          setTempEndDate(date);
                          setIsEndDatePickerOpen(false);
                        }}
                        onCalendarOpen={() => {
                          setIsEndDatePickerOpen(true);
                          setIsStartDatePickerOpen(false);
                        }}
                        onCalendarClose={() => setIsEndDatePickerOpen(false)}
                        open={isEndDatePickerOpen}
                        onClickOutside={() => setIsEndDatePickerOpen(false)}
                        onInputClick={() => {
                          if (isEndDatePickerOpen) {
                            setIsEndDatePickerOpen(false);
                          } else {
                            setIsEndDatePickerOpen(true);
                            setIsStartDatePickerOpen(false);
                          }
                        }}
                        selectsEnd
                        startDate={tempStartDate}
                        endDate={tempEndDate}
                        minDate={tempStartDate}
                        maxDate={new Date()}
                        className={styles['date-picker-input']}
                        dateFormat="dd/MM/yyyy"
                        placeholderText="End Date"
                        popperProps={{
                          positionFixed: true,
                          modifiers: [{
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                              padding: 5,
                            },
                          }],
                        }}
                        popperClassName={styles['date-picker-popper']}
                      />
                      <img 
                        src={calendarIcon} 
                        alt="Calendar" 
                        className={styles['calendar-icon']} 
                        onClick={() => {
                          if (isEndDatePickerOpen) {
                            setIsEndDatePickerOpen(false);
                          } else {
                            setIsEndDatePickerOpen(true);
                            setIsStartDatePickerOpen(false);
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                <div className={styles['date-range-filters']}>
                  {['1 Day', '7 Days', 'Month to Date', '1 Month', '2 Months', '3 Months', '4 Months', '5 Months', '6 Months'].map((range) => (
                    <button
                      key={range}
                      className={`${styles['date-range-filter']} ${selectedDateRange === range ? styles['active'] : ''}`}
                      onClick={() => handleDateRangeSelect(range)}
                    >
                      {range}
                    </button>
                  ))}
                </div>
                
                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px' }}>Region</label>
                  <div className={styles['custom-select-wrapper']}>
                    <div 
                      className={`${styles['custom-select']} ${isRegionDropdownOpen ? styles['open'] : ''}`}
                      onClick={() => setIsRegionDropdownOpen(!isRegionDropdownOpen)}
                    >
                      <div className={styles['selected-value']}>
                        {tempRegion === 'All Regions' ? tempRegion : `${tempRegion} (${getRegionName(tempRegion)})`}
                      </div>
                      <img 
                        src={dropdownIcon} 
                        alt="dropdown" 
                        className={`${styles['dropdown-icon']} ${isRegionDropdownOpen ? styles['rotate'] : ''}`} 
                      />
                      {isRegionDropdownOpen && (
                        <div className={styles['dropdown-options']}>
                          {regions.map(region => (
                            <div 
                              key={region}
                              className={`${styles['dropdown-option']} ${tempRegion === region ? styles['selected'] : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setTempRegion(region);
                                setIsRegionDropdownOpen(false);
                              }}
                            >
                              {region === 'All Regions' ? region : `${region} (${getRegionName(region)})`}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className={styles['modal-actions']}>
                <button 
                  type="button" 
                  className={styles['clear-button']}
                  onClick={clearFilters}
                >
                  Clear
                </button>
                <button 
                  type="button" 
                  className={styles['apply-button']}
                  onClick={applyFilters}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AwsCostDashboard;
